/**
 * Client-portal API client — a SEPARATE axios instance from the staff `useApi`.
 * It reads ONLY the namespaced portal token keys (never the staff `accessToken`),
 * so a builder who is also a homeowner in the same browser keeps two independent
 * sessions. Auth endpoints are tenant-scoped (`/:tenantSlug/auth/*`); data
 * endpoints resolve the tenant from the token.
 */
import axios, { type AxiosInstance } from 'axios';

const PORTAL_BASE_URL = `${import.meta.env.VITE_API_URL || '/api'}/portal`;

export const PORTAL_KEYS = {
  access: 'portalAccessToken',
  refresh: 'portalRefreshToken',
  slug: 'portalTenantSlug',
} as const;

/**
 * Staff "View as client" preview session — stored in sessionStorage (tab-scoped,
 * gone when the tab closes) so it NEVER clobbers a real client login persisted
 * in localStorage. When a preview token is present it wins for this tab.
 */
export const PORTAL_PREVIEW_KEYS = {
  access: 'portalPreviewToken',
  slug: 'portalPreviewSlug',
} as const;

export function isPortalPreview(): boolean {
  return !!sessionStorage.getItem(PORTAL_PREVIEW_KEYS.access);
}

export function clearPortalPreview(): void {
  sessionStorage.removeItem(PORTAL_PREVIEW_KEYS.access);
  sessionStorage.removeItem(PORTAL_PREVIEW_KEYS.slug);
}

let client: AxiosInstance | null = null;

function getClient(): AxiosInstance {
  if (client) return client;
  client = axios.create({
    baseURL: PORTAL_BASE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    // A tab-scoped preview token (staff "View as client") takes precedence.
    const token = sessionStorage.getItem(PORTAL_PREVIEW_KEYS.access) || localStorage.getItem(PORTAL_KEYS.access);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      // Preview sessions have no refresh token — when they expire, end the
      // preview instead of silently refreshing into the REAL client session.
      if (error.response?.status === 401 && isPortalPreview()) {
        const pslug = sessionStorage.getItem(PORTAL_PREVIEW_KEYS.slug);
        clearPortalPreview();
        if (pslug) window.location.href = `/portal/${pslug}/login?preview=expired`;
        return Promise.reject(error);
      }
      const slug = localStorage.getItem(PORTAL_KEYS.slug);
      if (error.response?.status === 401 && original && !original._retry && slug) {
        original._retry = true;
        try {
          const refreshToken = localStorage.getItem(PORTAL_KEYS.refresh);
          if (refreshToken) {
            const { data } = await axios.post(`${PORTAL_BASE_URL}/${slug}/auth/refresh`, { refreshToken });
            localStorage.setItem(PORTAL_KEYS.access, data.data.accessToken);
            if (original.headers) original.headers.Authorization = `Bearer ${data.data.accessToken}`;
            return client!.request(original);
          }
        } catch {
          localStorage.removeItem(PORTAL_KEYS.access);
          localStorage.removeItem(PORTAL_KEYS.refresh);
          if (slug) window.location.href = `/portal/${slug}/login`;
        }
      }
      return Promise.reject(error);
    },
  );
  return client;
}

export interface PortalAuthResult {
  accessToken: string;
  refreshToken: string;
  portalUser: { id: string; email: string; name: string | null };
  tenant: { slug: string };
}

export interface PortalJobCard {
  jobNo: string;
  name: string | null;
  status: string | null;
  siteAddress: string | null;
  progressPercent: number;
  stagesReached: number;
  stagesTotal: number;
  /** Variations awaiting an answer + selections still open. */
  needsAttention: number;
}
export interface PortalJobSummary {
  jobNo: string;
  name: string | null;
  status: string | null;
  siteAddress: string | null;
  /** GST-inclusive contract value; the portal never carries ex-GST money. */
  contractIncGst: number | null;
  photoCount: number;
  documentCount: number;
  pendingVariationCount: number;
  attention: { variationsAwaiting: number; selectionsOpen: number; selectionsOverdue: number; nextSelectionDeadline: string | null; nextSelectionTitle: string | null; messagesUnread: number };
}
export interface PortalMessage {
  id: string;
  jobId: string;
  direction: 'client' | 'builder';
  authorName: string | null;
  body: string;
  createdAt: string;
  readAt: string | null;
}
export interface PortalActivity {
  type: 'milestone' | 'document' | 'variation' | 'photo' | string;
  title: string;
  at: string | null;
}
export interface PortalStage {
  stageNumber: number;
  stageName: string | null;
  percentage: number | null;
  /** GST-inclusive — the figure on the client's invoice. The portal never carries ex-GST money. */
  amountIncGst: number | null;
  claimedDate: string | null;
  paidDate: string | null;
  status: string | null;
  /** The stage has been claimed — the build got there. Paid is a separate, quieter state. */
  reached: boolean;
  /** The posted claim's transaction number — set once claimed; fetch its tax invoice by it. */
  tranNo: number | null;
}
export interface PortalTask {
  title: string;
  status: string | null;
  percentComplete: number;
  startDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
}
export interface PortalPhoto {
  photoId: string;
  caption: string | null;
  logDate: string | null;
  mimeType: string | null;
}
export interface PortalInspection {
  inspectionId: string;
  templateName: string;
  category: string;
  overallResult: string | null;
  passCount: number;
  failCount: number;
  submittedAt: string | null;
}

export interface PortalDocument {
  documentId: string;
  fileName: string;
  mimeType: string | null;
  documentType: string | null;
  createdAt: string | null;
}
export interface PortalVariationLine {
  description: string | null;
  lineType: 'debit' | 'credit' | string;
  /** GST-inclusive, with the variation's markup applied — the lines add up to totalIncGst. */
  amountIncGst: number | null;
}
export interface PortalVariation {
  voId: string;
  voNumber: number;
  description: string | null;
  additionalText: string | null;
  totalIncGst: number | null;
  status: string;
  clientAccepted: boolean;
  clientAcceptedDate: string | null;
  clientResponse: 'approved' | 'declined' | null;
  declineReason: string | null;
  lines: PortalVariationLine[];
}

export interface PortalContactInfo {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}
export interface PortalLink {
  label: string;
  url: string;
  description?: string;
}
export interface PortalFaqItem {
  question: string;
  answer: string;
}
export interface PortalInfo {
  contacts: PortalContactInfo[];
  usefulLinks: PortalLink[];
  faqs: PortalFaqItem[];
  faqHtml: string | null;
}
export interface PortalBranding {
  companyName: string | null;
  logoDataUri: string | null;
  brandColor: string | null;
}
export interface PortalSelectionOption {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  isStandardInclusion: boolean;
  /** GST-inclusive upgrade price over the included option. */
  extraIncGst: number;
}
export interface PortalSelection {
  billItemId: string;
  title: string;
  category: string | null;
  required: boolean;
  deadline: string | null;
  /** GST-inclusive allowance the contract already includes for this item. */
  includedIncGst: number;
  selectedOptionId: string | null;
  selectedAt: string | null;
  clientNote: string | null;
  options: PortalSelectionOption[];
}

export const portalApi = {
  // Public (no token): builder branding for white-labelling login + app shell.
  async getBranding(tenantSlug: string): Promise<PortalBranding> {
    const { data } = await getClient().get(`/${tenantSlug}/auth/branding`);
    return data.data;
  },
  async login(tenantSlug: string, email: string, password: string): Promise<PortalAuthResult> {
    const { data } = await getClient().post(`/${tenantSlug}/auth/login`, { email, password });
    return data.data;
  },
  async acceptInvite(
    tenantSlug: string,
    token: string,
    password: string,
    name?: string,
  ): Promise<PortalAuthResult> {
    const { data } = await getClient().post(`/${tenantSlug}/auth/accept-invite`, { token, password, name });
    return data.data;
  },
  async forgotPassword(tenantSlug: string, email: string): Promise<{ success: boolean; message?: string }> {
    const { data } = await getClient().post(`/${tenantSlug}/auth/forgot-password`, { email });
    return data;
  },
  async resetPassword(tenantSlug: string, token: string, password: string): Promise<{ success: boolean; message?: string }> {
    const { data } = await getClient().post(`/${tenantSlug}/auth/reset-password`, { token, password });
    return data;
  },
  async logout(tenantSlug: string, refreshToken: string): Promise<void> {
    await getClient().post(`/${tenantSlug}/auth/logout`, { refreshToken });
  },
  async updateMe(input: { name?: string; currentPassword?: string; newPassword?: string }): Promise<{ name: string | null }> {
    const { data } = await getClient().patch('/me', input);
    return data.data;
  },
  async me(): Promise<{
    user: { id: string; email: string; name: string | null; jobCount: number };
    tenant: { slug: string };
    preview?: boolean;
  }> {
    const { data } = await getClient().get('/me');
    return data.data;
  },
  async getInfo(): Promise<PortalInfo> {
    const { data } = await getClient().get('/info');
    return data.data;
  },
  async getSelections(jobNo: string): Promise<PortalSelection[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/selections`);
    return data.data;
  },
  async chooseSelection(jobNo: string, billItemId: string, optionId: string, note?: string): Promise<void> {
    await getClient().post(`/jobs/${encodeURIComponent(jobNo)}/selections/${billItemId}/choose`, { optionId, note });
  },
  async getJobs(): Promise<PortalJobCard[]> {
    const { data } = await getClient().get('/jobs');
    return data.data;
  },
  async getJob(jobNo: string): Promise<PortalJobSummary> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}`);
    return data.data;
  },
  async getProgress(jobNo: string): Promise<{ stages: PortalStage[]; tasks: PortalTask[] }> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/progress`);
    return data.data;
  },
  async getActivity(jobNo: string): Promise<PortalActivity[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/activity`);
    return data.data;
  },
  async getPhotos(jobNo: string): Promise<PortalPhoto[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/photos`);
    return data.data;
  },
  // Photos require an authenticated fetch (portal token) → object URL; a plain
  // <img src> can't send the bearer header.
  async getPhotoBlobUrl(jobNo: string, photoId: string): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/photos/${photoId}/blob`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(res.data as Blob);
  },
  async getPhotoThumbUrl(jobNo: string, photoId: string): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/photos/${photoId}/thumb`, { responseType: 'blob' });
    return URL.createObjectURL(res.data as Blob);
  },
  async getMessages(jobNo: string): Promise<PortalMessage[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/messages`);
    return data.data;
  },
  async postMessage(jobNo: string, body: string): Promise<PortalMessage> {
    const { data } = await getClient().post(`/jobs/${encodeURIComponent(jobNo)}/messages`, { body });
    return data.data;
  },
  async getInspections(jobNo: string): Promise<PortalInspection[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/inspections`);
    return data.data;
  },
  async getInspectionPdfUrl(jobNo: string, inspectionId: string): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/inspections/${inspectionId}/pdf`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(res.data as Blob);
  },
  async getDocuments(jobNo: string): Promise<PortalDocument[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/documents`);
    return data.data;
  },
  /** Object URL of a shared document for the in-portal viewer (PDF / image). Caller revokes. */
  async getDocumentViewUrl(jobNo: string, documentId: string): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/documents/${documentId}/download`, { params: { inline: '1' }, responseType: 'blob' });
    return URL.createObjectURL(res.data as Blob);
  },
  async downloadDocument(jobNo: string, documentId: string, fileName: string): Promise<void> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  async getVariations(jobNo: string): Promise<PortalVariation[]> {
    const { data } = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/variations`);
    return data.data;
  },
  // Variation PDF requires the portal bearer token → fetch as blob → object URL.
  async getVariationPdfUrl(jobNo: string, voId: string): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/variations/${voId}/pdf`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(res.data as Blob);
  },
  /** The tax invoice for a claimed stage. */
  async getClaimPdfUrl(jobNo: string, tranNo: number): Promise<string> {
    const res = await getClient().get(`/jobs/${encodeURIComponent(jobNo)}/claims/${tranNo}/pdf`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(res.data as Blob);
  },
  async approveVariation(jobNo: string, voId: string, note?: string): Promise<void> {
    await getClient().post(`/jobs/${encodeURIComponent(jobNo)}/variations/${voId}/approve`, { note });
  },
  async declineVariation(jobNo: string, voId: string, reason: string): Promise<void> {
    await getClient().post(`/jobs/${encodeURIComponent(jobNo)}/variations/${voId}/decline`, { reason });
  },
};
